import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import {
  BookOpen,
  Save,
  Printer,
  Play,
  Pause,
  Pencil,
  X,
  Plus,
  Trash2,
  Music,
  Mic,
  MicOff,
  RotateCcw,
  Headphones,
  GraduationCap,
} from "lucide-react";
import { LearnWorkbenchShell } from "../components/layout/LearnWorkbenchShell";
import { SongEditor } from "../components/songbook/SongEditor";
import { CanvasEditor } from "../components/songbook/CanvasEditor";
import { ChordProPreview } from "../components/songbook/ChordProPreview";
import { MiniChordNeck } from "../components/songbook/MiniChordNeck";
import { YouTubeSync } from "../components/songbook/YouTubeSync";
import { YouTubeMiniPlayer, type YouTubeMiniPlayerRef } from "../components/songbook/YouTubeMiniPlayer";
import { parseChordPro } from "../lib/chordProParser";
import { useSongbookLibrary, type SongbookEntry } from "../hooks/useSongbookLibrary";
import { usePracticeSessions } from "../hooks/usePracticeSessions";
import { useAudioEngine } from "../hooks/useAudioEngine";
import { useGuitarSynth } from "../hooks/useGuitarSynth";
import { useStringSpectrum } from "../hooks/useStringSpectrum";
import {
  useRealtimeChordDetector,
  type RealtimeDetectedChord,
} from "../hooks/useRealtimeChordDetector";
import { parseChordName, type ParsedChord } from "../lib/musicTheory";
import { getClassicVoicings } from "../lib/chordEngine";
import { noteToSemitones, semitonesToNote } from "../lib/guitarEngine";
import { StringSpectrum } from "../components/songbook/StringSpectrum";
import { MicStatusIndicator, type MicState } from "../components/songbook/MicStatusIndicator";
import { usePracticeMode } from "../contexts/PracticeModeContext";
import { useStudentAuth } from "../hooks/useStudentAuth";

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

/** Umbrales centralizados de confianza para detección de acordes */
const CONFIDENCE_THRESHOLDS = {
  LEARN_MODE: 0.50,
  PLAY_MODE_REALTIME: 0.45,
  PLAY_MODE_INLINE_RESUME: 0.40,
  PLAY_MODE_INLINE_START: 0.35,
} as const;

const EMPTY_SONG_TEMPLATE = `{title: }
{artist: }
{key: C}
{tempo: 120}

`;

/**
 * Formatea un acorde parseado al mismo formato que useRealtimeChordDetector
 * para permitir comparación directa.
 */
function formatChordNameForCompare(parsed: ParsedChord): string {
  const rootSharp = semitonesToNote(noteToSemitones(parsed.root), false);
  switch (parsed.quality) {
    case "major": return rootSharp;
    case "minor": return `${rootSharp}m`;
    case "dim": return `${rootSharp}dim`;
    case "aug": return `${rootSharp}aug`;
    case "sus2": return `${rootSharp}sus2`;
    case "sus4": return `${rootSharp}sus4`;
    default: return `${rootSharp}${parsed.quality}`;
  }
}

/**
 * Verifica si un acorde detectado es equivalente al esperado.
 * Permite extensiones: C7 ≈ C, Cm7 ≈ Cm, etc.
 */
function chordsAreEquivalent(detected: string, expected: string): boolean {
  if (detected === expected) return true;

  // Extraer raíz del detectado (todo antes de la primera minúscula o número)
  const detectedRootMatch = detected.match(/^([A-G][#b]?)/);
  const expectedRootMatch = expected.match(/^([A-G][#b]?)/);
  if (!detectedRootMatch || !expectedRootMatch) return false;
  const dRoot = detectedRootMatch[1];
  const eRoot = expectedRootMatch[1];
  if (dRoot !== eRoot) return false;

  const dQuality = detected.slice(dRoot.length);
  const eQuality = expected.slice(eRoot.length);

  // Mayor: C7, Cmaj7, Cadd9, C6, C9, C11, C13, Cadd2, Cadd4, Cadd11 ≈ C
  const majorExtensions = ["", "7", "maj7", "add9", "add2", "add4", "add11", "6", "9", "11", "13"];
  if (eQuality === "" && majorExtensions.includes(dQuality)) return true;

  // Menor: Cm7, CmMaj7, Cm6, Cm9, Cm11, Cm13 ≈ Cm
  const minorExtensions = ["m", "m7", "mMaj7", "m6", "m9", "m11", "m13"];
  if (eQuality === "m" && minorExtensions.includes(dQuality)) return true;

  // Disminuido: Cdim7 ≈ Cdim
  if (eQuality === "dim" && (dQuality === "dim" || dQuality === "dim7")) return true;

  // Aumentado: Caug7 ≈ Caug
  if (eQuality === "aug" && (dQuality === "aug" || dQuality === "aug7")) return true;

  // Sus2: C7sus2 ≈ Csus2
  if (eQuality === "sus2" && (dQuality === "sus2" || dQuality === "7sus2")) return true;

  // Sus4: C7sus4 ≈ Csus4
  if (eQuality === "sus4" && (dQuality === "sus4" || dQuality === "7sus4")) return true;

  return false;
}

export const SongbookPage: React.FC = () => {
  const [raw, setRaw] = useState(EMPTY_SONG_TEMPLATE);
  const [mode, setMode] = useState<"edit" | "play">("edit");
  const [saveOpen, setSaveOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [selectedSongId, setSelectedSongId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Modo practica
  const [practiceScore, setPracticeScore] = useState(0);
  const [practiceAttempts, setPracticeAttempts] = useState(0);
  const [practiceStartTime, setPracticeStartTime] = useState<number | null>(null);
  const [currentChordIndex, setCurrentChordIndex] = useState(0);
  const [detectedChord, setDetectedChord] = useState<string | null>(null);
  const [practiceFinished, setPracticeFinished] = useState(false);

  // Acorde seleccionado para ver diagrama
  const [selectedChord, setSelectedChord] = useState<string | null>(null);

  // Tracking por compás y fluidez
  const [barDetections, setBarDetections] = useState<Map<number, boolean>>(new Map());
  const [flowScore, setFlowScore] = useState(0);
  const [sessionLog, setSessionLog] = useState<{ chordIndex: number; detected: boolean; timestamp: number; clarity: number }[]>([]);
  const [detectedInCurrentBar, setDetectedInCurrentBar] = useState(false);

  // Auth
  const { user, isAuthenticated } = useStudentAuth();

  // Refs para acceso desde callbacks del detector en tiempo real
  const currentChordIndexRef = useRef(currentChordIndex);
  useEffect(() => { currentChordIndexRef.current = currentChordIndex; }, [currentChordIndex]);

  const song = useMemo(() => parseChordPro(raw), [raw]);
  const { items: savedSongs, loading: songsLoading, saveSong, deleteSong } = useSongbookLibrary();
  const { sessions: practiceSessions, saveSession } = usePracticeSessions();

  // Deben declararse ANTES de hasUnsavedChanges y chordSequence para evitar TDZ
  const [chordTimings, setChordTimings] = useState<number[]>([]);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [youtubeTimestamps, setYoutubeTimestamps] = useState<number[]>([]);

  // Indicador de cambios sin guardar
  const hasUnsavedChanges = useMemo(() => {
    if (!selectedSongId) return false;
    const entry = savedSongs.find((s) => s.id === selectedSongId);
    if (!entry) return false;
    return (
      entry.content !== raw ||
      JSON.stringify(entry.chordTimings) !== JSON.stringify(chordTimings) ||
      entry.youtubeUrl !== youtubeUrl ||
      JSON.stringify(entry.youtubeTimestamps) !== JSON.stringify(youtubeTimestamps)
    );
  }, [selectedSongId, savedSongs, raw, chordTimings, youtubeUrl, youtubeTimestamps]);

  // Cargar primera canción guardada automáticamente (solo una vez al inicio)
  const hasAutoLoaded = useRef(false);
  useEffect(() => {
    if (!songsLoading && savedSongs.length > 0 && !hasAutoLoaded.current && !selectedSongId) {
      hasAutoLoaded.current = true;
      loadSong(savedSongs[0]);
    }
  }, [songsLoading, savedSongs]);

  const {
    state: engineState,
    init,
    detectChord,
  } = useAudioEngine();

  const { state: rtState, start: startRtDetector, stop: stopRtDetector } =
    useRealtimeChordDetector();

  // Ref para acceder al volumen actual desde callbacks del detector
  const rtVolumeRef = useRef(rtState.volume);
  useEffect(() => { rtVolumeRef.current = rtState.volume; }, [rtState.volume]);

  const { playNote } = useGuitarSynth();

  const chords = useMemo(() => Array.from(song.chords), [song.chords]);

  /** Secuencia de acordes con duración variable (en beats). Por defecto 4 beats = 1 compás. */
  const chordSequence = useMemo(() => {
    interface ChordEvent { chord: string; durationBeats: number }
    const seq: ChordEvent[] = [];
    for (const line of song.lines) {
      if (line.type === "lyric") {
        for (const seg of line.segments) {
          if (seg.chord) {
            seq.push({ chord: seg.chord, durationBeats: 4 });
          }
        }
      }
    }
    // Si hay timings guardados, aplicarlos
    if (chordTimings.length > 0) {
      for (let i = 0; i < seq.length && i < chordTimings.length; i++) {
        seq[i].durationBeats = chordTimings[i];
      }
    }
    return seq.length > 0 ? seq : chords.map((c) => ({ chord: c, durationBeats: 4 }));
  }, [song.lines, chords, chordTimings]);

  const currentChord = chordSequence[currentChordIndex]?.chord || null;
  const nextPracticeChord = chordSequence[currentChordIndex + 1]?.chord || null;

  const currentPracticeVoicing = useMemo(() => {
    if (!currentChord) return null;
    const parsed = parseChordName(currentChord);
    if (!parsed) return null;
    return getClassicVoicings(parsed.root, parsed.quality)[0] || null;
  }, [currentChord]);

  const nextPracticeVoicing = useMemo(() => {
    if (!nextPracticeChord) return null;
    const parsed = parseChordName(nextPracticeChord);
    if (!parsed) return null;
    return getClassicVoicings(parsed.root, parsed.quality)[0] || null;
  }, [nextPracticeChord]);

  const selectedChordVoicing = useMemo(() => {
    if (!selectedChord) return null;
    const parsed = parseChordName(selectedChord);
    if (!parsed) return null;
    return getClassicVoicings(parsed.root, parsed.quality)[0] || null;
  }, [selectedChord]);

  const [saveError, setSaveError] = useState<string | null>(null);

  const handleSave = async (overrides?: { chordTimings?: number[]; youtubeTimestamps?: number[] }) => {
    if (isSaving) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      const savedId = await saveSong({
        id: selectedSongId || undefined,
        title: title || song.title || "Sin titulo",
        artist: artist || song.artist || "",
        content: raw,
        capo: song.capo,
        key: song.key,
        tempo: song.tempo,
        chordTimings: overrides?.chordTimings ?? (chordTimings.length > 0 ? chordTimings : undefined),
        youtubeUrl: youtubeUrl || undefined,
        youtubeTimestamps: overrides?.youtubeTimestamps ?? (youtubeTimestamps.length > 0 ? youtubeTimestamps : undefined),
      });
      if (!savedId) {
        setSaveError("No se pudo guardar. Verifica tu conexión o inicia sesión.");
        return;
      }
      // Si era una canción nueva, setear el ID para futuras actualizaciones
      if (!selectedSongId) {
        setSelectedSongId(savedId);
      }
      setSaveOpen(false);
      setTitle("");
      setArtist("");
    } finally {
      setIsSaving(false);
    }
  };

  const loadSong = (entry: SongbookEntry) => {
    setRaw(entry.content);
    setSelectedSongId(entry.id);
    setMode("edit");
    setChordTimings(entry.chordTimings || []);
    setYoutubeUrl(entry.youtubeUrl || "");
    setYoutubeTimestamps(entry.youtubeTimestamps || []);
  };

  const newSong = () => {
    setRaw(`{title: Nueva cancion}
{artist: }
{key: C}

`);
    setSelectedSongId(null);
    setMode("edit");
  };

  const handleBpmChange = useCallback((val: number) => {
    const clamped = Math.max(40, Math.min(200, val));
    setBpm(clamped);
    // Recalcular duraciones desde timestamps de YouTube si existen
    if (youtubeTimestamps.length >= 2) {
      const newTimings: number[] = [];
      for (let i = 0; i < youtubeTimestamps.length - 1; i++) {
        const seconds = youtubeTimestamps[i + 1] - youtubeTimestamps[i];
        const beats = Math.max(1, Math.round(seconds * clamped / 60));
        newTimings.push(beats);
      }
      setChordTimings(newTimings);
    }
  }, [youtubeTimestamps]);

  /* ═══════════════════════════════════════════
     SUBMODOS: Aprender vs Practicar
     ═══════════════════════════════════════════ */
  const [subMode, setSubMode] = useState<"learn" | "play" | null>(null);
  const [bpm, setBpm] = useState(song.tempo);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  // Integración con modo global de práctica (appbar)
  const { mode: practiceMode } = usePracticeMode();
  useEffect(() => {
    if (practiceMode === "practice") {
      setMode("play");
      setSubMode("play");
    } else if (practiceMode === "learn") {
      setMode("play");
      setSubMode("learn");
    }
  }, [practiceMode]);

  // Sincronizar acorde actual con timestamp de YouTube
  useEffect(() => {
    if (!isPlaying || youtubeTimestamps.length === 0) return;
    const sync = () => {
      if (!youtubePlayerRef.current) return;
      if (currentChordIndex >= 0 && currentChordIndex < youtubeTimestamps.length) {
        const ts = youtubeTimestamps[currentChordIndex];
        if (typeof ts === "number" && ts >= 0) {
          youtubePlayerRef.current.seekTo(ts);
        }
      }
    };
    // Retry inmediato + delayed para asegurar que el reproductor esté listo
    sync();
    const t = setTimeout(sync, 500);
    const t2 = setTimeout(sync, 1500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [currentChordIndex, isPlaying, youtubeTimestamps]);

  const { energies: spectrumEnergies, volume: micVolume } = useStringSpectrum(isPlaying && subMode === "play");

  const listenIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const metronomeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const metronomeChordIdxRef = useRef(0);
  const metronomeBeatRef = useRef(0);
  const metronomeGlobalBeatRef = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const youtubePlayerRef = useRef<YouTubeMiniPlayerRef | null>(null);

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    }
    if (audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playClick = useCallback((accent = false) => {
    const ctx = getAudioCtx();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = accent ? "square" : "sine";
    osc.frequency.value = accent ? 1200 : 800;
    gain.gain.setValueAtTime(accent ? 0.15 : 0.08, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.06);
  }, [getAudioCtx]);

  const stopPractice = useCallback(() => {
    setIsPlaying(false);
    stopRtDetector();
    if (listenIntervalRef.current) {
      clearTimeout(listenIntervalRef.current);
      listenIntervalRef.current = null;
    }
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
    // Calcular fluidez al finalizar
    let finalFlowScore = 0;
    if (barDetections.size > 0) {
      const detected = Array.from(barDetections.values()).filter(Boolean).length;
      finalFlowScore = Math.round((detected / barDetections.size) * 100);
      setFlowScore(finalFlowScore);
    }
    setPracticeFinished(true);
    // Guardar sesión de práctica
    if (practiceStartTime && selectedSongId) {
      const duration = Math.floor((Date.now() - practiceStartTime) / 1000);
      saveSession({
        songId: selectedSongId,
        songTitle: song.title || "Sin titulo",
        mode: subMode || "play",
        bpm,
        score: practiceScore,
        attempts: practiceAttempts,
        flowScore: finalFlowScore,
        duration,
        sessionLog,
      }).catch(() => {});
    }
  }, [barDetections, stopRtDetector, practiceStartTime, selectedSongId, song.title, subMode, bpm, practiceScore, practiceAttempts, sessionLog, saveSession]);

  // Ref que siempre apunta a la última versión de stopPractice
  const stopPracticeRef = useRef<() => void>(stopPractice);
  stopPracticeRef.current = stopPractice;

  const startLearn = useCallback(async () => {
    if (!engineState.isReady) await init();
    setSubMode("learn");
    setMode("play");
    setIsPlaying(true);
    setPracticeScore(0);
    setPracticeAttempts(0);
    setPracticeStartTime(Date.now());
    setCurrentChordIndex(0);
    setDetectedChord(null);
    setPracticeFinished(false);
    setBarDetections(new Map());
    setFlowScore(0);
    setSessionLog([]);
    setDetectedInCurrentBar(false);
    // Reproducir video automáticamente si hay timestamps (con retry)
    const tryPlayVideo = () => {
      if (youtubePlayerRef.current && youtubeTimestamps.length > 0) {
        youtubePlayerRef.current.playVideo();
      }
    };
    tryPlayVideo();
    setTimeout(tryPlayVideo, 500);
    setTimeout(tryPlayVideo, 1500);
    // Iniciar detector en tiempo real
    const learnStartTime = Date.now();
    await startRtDetector((chord) => {
      if (!chord) return;
      // Ignorar detecciones durante el primer segundo y si el volumen es muy bajo
      if (Date.now() - learnStartTime < 1000) return;
      if (rtVolumeRef.current < 0.05) return;
      // Verificar si el acorde detectado coincide con el esperado
      const expectedEvent = chordSequence[currentChordIndexRef.current];
      if (!expectedEvent) return;
      const expected = expectedEvent.chord;
      const parsedExpected = parseChordName(expected);
      if (!parsedExpected) return;
      const detectedName = chord.name;
      const expectedName = formatChordNameForCompare(parsedExpected);
      if (chordsAreEquivalent(detectedName, expectedName) && chord.confidence > CONFIDENCE_THRESHOLDS.LEARN_MODE) {
        setDetectedChord(expected);
        setPracticeAttempts((a) => a + 1);
        setPracticeScore((s) => s + 1);
        const next = currentChordIndexRef.current + 1;
        if (next >= chordSequence.length) {
          stopPracticeRef.current();
          return;
        }
        setCurrentChordIndex(next);
        setDetectedChord(null);
      }
    });
  }, [engineState.isReady, init, chordSequence, startRtDetector, youtubeTimestamps]);

  const startPlay = useCallback(async () => {
    if (!engineState.isReady) await init();
    setSubMode("play");
    setMode("play");
    setIsPlaying(true);
    setPracticeScore(0);
    setPracticeAttempts(0);
    setPracticeStartTime(Date.now());
    setCurrentChordIndex(0);
    setCurrentBeat(0);
    setDetectedChord(null);
    setPracticeFinished(false);
    setBarDetections(new Map());
    setFlowScore(0);
    setSessionLog([]);
    setDetectedInCurrentBar(false);
    // Reproducir video automáticamente si hay timestamps (con retry)
    const tryPlayVideo = () => {
      if (youtubePlayerRef.current && youtubeTimestamps.length > 0) {
        youtubePlayerRef.current.playVideo();
      }
    };
    tryPlayVideo();
    setTimeout(tryPlayVideo, 500);
    setTimeout(tryPlayVideo, 1500);
    // Iniciar detector en tiempo real para modo practicar
    const playStartTime = Date.now();
    await startRtDetector((chord) => {
      if (!chord) return;
      // Ignorar detecciones durante el primer segundo y si el volumen es muy bajo
      if (Date.now() - playStartTime < 1000) return;
      if (rtVolumeRef.current < 0.05) return;
      const expectedEvent = chordSequence[currentChordIndexRef.current];
      if (!expectedEvent) return;
      const expected = expectedEvent.chord;
      const parsedExpected = parseChordName(expected);
      if (!parsedExpected) return;
      const detectedName = chord.name;
      const expectedName = formatChordNameForCompare(parsedExpected);
      if (chordsAreEquivalent(detectedName, expectedName) && chord.confidence > CONFIDENCE_THRESHOLDS.PLAY_MODE_REALTIME) {
        setDetectedChord(expected);
        setDetectedInCurrentBar(true);
      }
    });
  }, [engineState.isReady, init, chordSequence, startRtDetector, youtubeTimestamps]);

  const resetPractice = useCallback(() => {
    stopRtDetector();
    setPracticeScore(0);
    setPracticeAttempts(0);
    setPracticeStartTime(null);
    setCurrentChordIndex(0);
    setCurrentBeat(0);
    setDetectedChord(null);
    setPracticeFinished(false);
    setIsPlaying(false);
    setSubMode(null);
    setBarDetections(new Map());
    setFlowScore(0);
    setSessionLog([]);
    setDetectedInCurrentBar(false);
    if (listenIntervalRef.current) {
      clearTimeout(listenIntervalRef.current);
      listenIntervalRef.current = null;
    }
    if (metronomeIntervalRef.current) {
      clearInterval(metronomeIntervalRef.current);
      metronomeIntervalRef.current = null;
    }
  }, [stopRtDetector]);

  /* ── Modo APRENDER: detección en tiempo real vía useRealtimeChordDetector ── */

  /* ── Modo PRACTICAR: metrónomo + avance automático por duración variable ── */
  useEffect(() => {
    if (!isPlaying || subMode !== "play") return;

    const intervalMs = Math.max(100, 60000 / bpm);
    // Inicializar refs desde el state actual
    metronomeChordIdxRef.current = currentChordIndex;
    metronomeBeatRef.current = 0;
    metronomeGlobalBeatRef.current = 0;

    metronomeIntervalRef.current = setInterval(() => {
      const chordIdx = metronomeChordIdxRef.current;
      const currentDuration = chordSequence[chordIdx]?.durationBeats || 4;
      const globalBeat = metronomeGlobalBeatRef.current;
      const isAccent = globalBeat % 4 === 0;
      playClick(isAccent);
      setCurrentBeat(globalBeat % 4);

      metronomeBeatRef.current++;
      metronomeGlobalBeatRef.current++;

      // Cuando se completa la duración del acorde actual, avanzar
      if (metronomeBeatRef.current >= currentDuration) {
        // Registrar detección del acorde que termina
        const prevIdx = chordIdx;
        if (prevIdx >= 0 && prevIdx < chordSequence.length) {
          const wasDetected = detectedInCurrentBar;
          setBarDetections((prev) => new Map(prev).set(prevIdx, wasDetected));
          setSessionLog((prev) => [
            ...prev,
            { chordIndex: prevIdx, detected: wasDetected, timestamp: Date.now(), clarity: wasDetected ? 1 : 0 },
          ]);
          if (wasDetected) {
            setPracticeScore((s) => s + 1);
          }
          setPracticeAttempts((a) => a + 1);
        }
        // Resetear para nuevo acorde
        setDetectedInCurrentBar(false);
        setDetectedChord(null);
        metronomeBeatRef.current = 0;

        metronomeChordIdxRef.current++;
        if (metronomeChordIdxRef.current >= chordSequence.length) {
          stopPractice();
          return;
        }
        setCurrentChordIndex(metronomeChordIdxRef.current);
      }
    }, intervalMs);

    return () => {
      if (metronomeIntervalRef.current) clearInterval(metronomeIntervalRef.current);
    };
  }, [isPlaying, subMode, bpm, currentChordIndex, chordSequence, playClick, stopPractice, detectedInCurrentBar]);

  /* ── Detección continua en modo PRACTICAR vía useRealtimeChordDetector ── */

  /* ── Contador fluido ── */
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!practiceStartTime || !isPlaying) return;
    const id = setInterval(() => setTick((t) => t + 1), 100);
    return () => clearInterval(id);
  }, [practiceStartTime, isPlaying]);

  const practiceElapsed = practiceStartTime
    ? Math.floor((Date.now() - practiceStartTime) / 1000)
    : 0;

  const sidebar = (
    <div className="space-y-3">
      <div className="text-center pb-3 border-b border-white/10">
        <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-slate-500">
          Mi biblioteca
        </div>
        <div className="mt-2 w-12 h-12 mx-auto rounded-full bg-[var(--bordona-accent)] text-white flex items-center justify-center text-xl shadow-lg">
          <BookOpen className="w-5 h-5" />
        </div>
        <div className="mt-1.5 text-sm font-serif font-bold italic text-slate-100">
          Songbook
        </div>
        <div className="text-[11px] font-sans text-slate-400">
          {savedSongs.length} canciones
        </div>
      </div>
      {/* Indicador de usuario */}
      {isAuthenticated && user ? (
        <div className="px-2 py-2 rounded-lg bg-white/[0.07] text-[11px] text-slate-300">
          <div className="font-semibold text-[var(--bordona-accent)]">{user.displayName || user.email || "Usuario"}</div>
          <div className="text-[10px] text-slate-400">{user.email || ""}</div>
        </div>
      ) : (
        <div className="px-2 py-2 rounded-lg bg-[var(--bordona-accent-soft)] text-[11px] text-slate-200">
          <div className="font-semibold">Inicia sesión</div>
          <div className="text-[10px]">Para guardar canciones y sincronizar</div>
        </div>
      )}
      <button
        onClick={newSong}
        className="w-full py-2 rounded-lg text-xs font-sans font-medium bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] transition-all flex items-center justify-center gap-1.5"
      >
        <Plus className="w-3.5 h-3.5" />
        Nueva cancion
      </button>
      {songsLoading ? (
        <div className="text-[11px] text-slate-400 text-center py-4">Cargando...</div>
      ) : savedSongs.length === 0 ? (
        <div className="text-[11px] text-slate-500 text-center py-4">
          No hay canciones guardadas
        </div>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {savedSongs.map((entry) => (
            <div
              key={entry.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] font-sans cursor-pointer transition-colors ${
                selectedSongId === entry.id
                  ? "bg-[var(--bordona-accent)]/10 text-[var(--bordona-accent)] font-semibold"
                  : "text-slate-300 hover:bg-white/[0.07]"
              }`}
            >
              <button onClick={() => loadSong(entry)} className="flex-1 text-left truncate">
                {entry.title}
              </button>
              <button
                onClick={() => deleteSong(entry.id)}
                className="text-slate-500 hover:text-[var(--bordona-accent)] transition-colors shrink-0"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const inspector = (
    <div className="space-y-3">
      <div className="p-4 rounded-2xl bg-white/[0.08] border border-white/10 shadow-none">
        <div className="flex items-start justify-between gap-2">
          <div className="text-xl font-serif font-bold italic text-slate-100 leading-tight">
            {song.title || "Sin titulo"}
          </div>
          {hasUnsavedChanges && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)] text-[9px] font-bold uppercase tracking-wider">
              Sin guardar
            </span>
          )}
        </div>
        {song.artist && <div className="mt-1 text-[11px] text-slate-400 font-sans">{song.artist}</div>}
        <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-400">
          {song.key && <span className="px-1.5 py-0.5 rounded bg-white/[0.07]">Tono: {song.key}</span>}
          {song.capo > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)] font-semibold">
              Capo {song.capo}
            </span>
          )}
          <span className="px-1.5 py-0.5 rounded bg-white/[0.07]">{song.tempo} BPM</span>
        </div>
      </div>
      {chords.length > 0 && (
        <div className="p-3 rounded-2xl bg-white/[0.08] border border-white/10 shadow-none">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            Acordes ({chords.length})
          </div>
          <div className="flex flex-wrap gap-1">
            {chords.sort().map((c) => (
              <span key={c} className="px-1.5 py-0.5 rounded bg-white/[0.07] text-[11px] font-semibold text-slate-300">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}
      {practiceFinished && (
        <div className="p-3 rounded-2xl bg-white/[0.07] text-center">
          <div className="text-sm font-sans font-bold text-slate-100">
            {practiceScore}/{practiceAttempts} aciertos
          </div>
          <div className="text-[10px] text-slate-400">
            Tiempo: {Math.floor(practiceElapsed / 60)}:{String(practiceElapsed % 60).padStart(2, "0")}
          </div>
          <div className="text-lg font-bold text-[var(--bordona-accent)]">
            {practiceAttempts > 0 ? Math.round((practiceScore / practiceAttempts) * 100) : 0}%
          </div>
          <div className="mt-2 pt-2 border-t border-white/10">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Fluidez</div>
            <div className={`text-lg font-bold ${flowScore >= 80 ? "text-[#10B981]" : flowScore >= 50 ? "text-[#F59E0B]" : "text-[#EF4444]"}`}>
              {flowScore}%
            </div>
            <div className="text-[9px] text-slate-400 leading-tight mt-1">
              Basado en la continuidad de acordes detectados por compás
            </div>
          </div>
          <button
            onClick={resetPractice}
            className="mt-2 w-full py-1.5 rounded-lg bg-[var(--bordona-accent)] text-white text-[11px] font-medium hover:bg-[var(--bordona-accent-hover)] transition-colors"
          >
            Reintentar
          </button>
        </div>
      )}
      {/* Historial de sesiones de práctica */}
      {selectedSongId && practiceSessions.filter((s) => s.songId === selectedSongId).length > 0 && (
        <div className="p-3 rounded-2xl bg-white/[0.08] border border-white/10 shadow-none">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            Historial de práctica
          </div>
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {practiceSessions
              .filter((s) => s.songId === selectedSongId)
              .slice(0, 5)
              .map((s) => (
                <div key={s.id} className="flex items-center justify-between text-[10px] py-1 border-b border-white/10 last:border-0">
                  <div className="flex items-center gap-1.5">
                    <span className={clsx("w-2 h-2 rounded-full", s.mode === "learn" ? "bg-[#3A6080]" : "bg-[#10B981]")} />
                    <span className="text-slate-300">{s.mode === "learn" ? "Aprender" : "Practicar"}</span>
                    <span className="text-slate-400">• {Math.floor(s.duration / 60)}:{String(s.duration % 60).padStart(2, "0")}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-bold text-[var(--bordona-accent)]">{s.score}/{s.attempts}</span>
                    <span className={clsx("font-bold", s.flowScore >= 80 ? "text-[#10B981]" : s.flowScore >= 50 ? "text-[#F59E0B]" : "text-[#EF4444]")}>
                      {s.flowScore}%
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {saveOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setSaveOpen(false)}
        >
          <div className="bg-slate-900 border border-white/12 rounded-2xl shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-serif font-bold italic text-slate-100">Guardar cancion</h3>
              <button
                onClick={() => setSaveOpen(false)}
                className="w-7 h-7 rounded-full bg-white/[0.08] flex items-center justify-center text-slate-400 hover:text-slate-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Titulo</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={song.title || "Titulo"}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-white/12 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--bordona-accent-border)]/30 bg-white/[0.07]"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Artista</label>
                <input
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  placeholder={song.artist || "Artista"}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-white/12 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--bordona-accent-border)]/30 bg-white/[0.07]"
                />
              </div>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="w-full py-2 rounded-lg bg-[var(--bordona-accent)] text-white text-sm font-medium hover:bg-[var(--bordona-accent-hover)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? "Guardando..." : "Guardar en mi biblioteca"}
              </button>
              {saveError && (
                <div className="text-[11px] text-[#EF4444] bg-[#EF4444]/10 rounded-lg px-3 py-2">
                  {saveError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <LearnWorkbenchShell
        sidebar={sidebar}
        inspector={inspector}
        title={song.title || "Songbook"}
        activeModule="songbook"
      >
        <div className="h-full overflow-hidden overflow-x-hidden">
          <AnimatePresence mode="wait">
            {mode === "edit" ? (
              <motion.div
                key="edit"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full p-4 flex flex-col gap-3 overflow-hidden"
              >
                <div className="flex-1 min-h-0">
                  <CanvasEditor raw={raw} onChange={setRaw} />
                </div>

                {/* ─── Panel de duraciones y YouTube ─── */}
                <div className="shrink-0 bg-white/[0.07] rounded-xl p-3 space-y-3 max-h-48 overflow-y-auto">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Duraciones de acordes (en beats)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {chordSequence.map((event, i) => (
                      <div key={i} className="flex items-center gap-1 bg-white/[0.08] rounded-lg px-2 py-1 border border-white/10">
                        <span className="text-[11px] font-bold text-[var(--bordona-accent)] min-w-[2rem]">{event.chord}</span>
                        <input
                          type="number"
                          min={1}
                          max={32}
                          value={event.durationBeats}
                          onChange={(e) => {
                            const val = Math.max(1, Math.min(32, parseInt(e.target.value) || 4));
                            setChordTimings((prev) => {
                              const next = [...prev];
                              next[i] = val;
                              return next;
                            });
                          }}
                          className="w-10 text-center text-[11px] bg-white/[0.07] rounded border border-white/10 focus:border-[var(--bordona-accent)] outline-none py-0.5"
                        />
                        <span className="text-[9px] text-slate-500">beats</span>
                      </div>
                    ))}
                  </div>

                  {/* YouTube URL */}
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-400">YouTube:</span>
                    <input
                      type="text"
                      placeholder="https://youtube.com/watch?v=..."
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      className={clsx(
                        "flex-1 text-[11px] bg-white/[0.07] rounded-lg border px-2 py-1 outline-none text-slate-100",
                        youtubeUrl && !extractYouTubeId(youtubeUrl)
                          ? "border-[#EF4444] focus:border-[#EF4444]"
                          : "border-white/10 focus:border-[var(--bordona-accent)]"
                      )}
                    />
                  </div>

                  {/* Sincronización con YouTube */}
                  {youtubeUrl && (
                    <YouTubeSync
                      youtubeUrl={youtubeUrl}
                      chordSequence={chordSequence}
                      bpm={song.tempo}
                      initialTimestamps={youtubeTimestamps}
                      onTimingsComputed={(timings, timestamps) => {
                        setChordTimings(timings);
                        setYoutubeTimestamps(timestamps);
                        // Guardar inmediatamente con los nuevos timings
                        handleSave({ chordTimings: timings, youtubeTimestamps: timestamps });
                      }}
                    />
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="play"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="h-full flex flex-col relative"
              >
                {/* Banner del acorde actual en modo práctica */}
                {isPlaying && currentChord && (
                  <div className="flex-shrink-0 px-4 py-2 bg-[var(--bordona-accent-soft)] border-b-2 border-[var(--bordona-accent)] flex items-center justify-center gap-3 z-10">
                    <span className="text-[11px] font-sans font-medium text-[var(--bordona-accent)] uppercase tracking-wider">
                      {subMode === "learn" ? "Toca ahora" : "Acorde actual"}
                    </span>
                    <span className="text-2xl font-serif font-bold text-[var(--bordona-accent)]">
                      {currentChord}
                    </span>
                    {detectedChord && (
                      <span className={clsx(
                        "text-sm font-sans font-bold px-2 py-0.5 rounded-full",
                        detectedChord === currentChord
                          ? "bg-[#10B981] text-white"
                          : "bg-[#EF4444] text-white"
                      )}>
                        Detectado: {detectedChord}
                      </span>
                    )}
                  </div>
                )}

                {/* Preview de la canción */}
                <div className="flex-1 overflow-y-auto relative">
                  <ChordProPreview
                    song={song}
                    activeChordIndex={currentChordIndex}
                    practiceMode={isPlaying}
                    currentChord={currentChord}
                    detectedChord={detectedChord}
                    onChordClick={(chord) => setSelectedChord(chord)}
                  />

                  {/* Panel flotante de diagrama de acorde */}
                  <AnimatePresence>
                    {selectedChord && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute bottom-4 right-4 z-30 bg-slate-900/95 backdrop-blur-md rounded-xl border border-white/12 shadow-xl p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold text-[var(--bordona-accent)]">{selectedChord}</span>
                          <button
                            onClick={() => setSelectedChord(null)}
                            className="w-5 h-5 rounded-full bg-white/[0.08] flex items-center justify-center text-slate-400 hover:text-slate-100"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                        <MiniChordNeck
                          voicing={selectedChordVoicing}
                          chordName={selectedChord}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* ═══ DEBUG VISOR ═══ */}
                  {isPlaying && (
                    <div className="absolute top-3 right-3 z-30 w-56 bg-black/80 backdrop-blur-md rounded-xl p-3 text-white text-[10px] font-mono space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Volumen</span>
                        <span>{(rtState.volume * 100).toFixed(1)}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-400 rounded-full transition-all"
                          style={{ width: `${Math.min(rtState.volume * 500, 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Detectado</span>
                        <span className={rtState.detectedChord ? "text-amber-400 font-bold" : "text-gray-500"}>
                          {rtState.detectedChord?.name || "—"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Confianza</span>
                        <span>{rtState.detectedChord ? (rtState.detectedChord.confidence * 100).toFixed(0) + "%" : "—"}</span>
                      </div>
                      {/* Chromagrama mini */}
                      <div className="flex items-end gap-0.5 h-10 pt-1">
                        {rtState.chromagram.map((val, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                            <div
                              className="w-full bg-amber-400/70 rounded-sm transition-all"
                              style={{ height: `${val * 100}%` }}
                            />
                            <span className="text-[7px] text-gray-400">{["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"][i]}</span>
                          </div>
                        ))}
                      </div>
                      <div className="text-[8px] text-gray-500 text-center pt-1">
                        Esperado: {currentChord || "—"}
                      </div>
                    </div>
                  )}
                </div>

                {/* ─── Barra flotante estilo Escalas ─── */}
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20">
                  <div
                    className="flex items-center gap-3 px-5 py-3 rounded-full bg-slate-950/88 backdrop-blur-xl border border-white/12 shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
                    style={{ boxShadow: "0 8px 32px rgba(45,42,38,0.1)" }}
                  >
                    {/* Volver a editar */}
                    <button
                      onClick={() => { setMode("edit"); resetPractice(); }}
                      className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/10 transition-colors"
                      title="Editar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>

                    <div className="w-px h-5 bg-white/15" />

                    {/* Submodos */}
                    {!subMode && (
                      <>
                        <button
                          onClick={startLearn}
                          disabled={engineState.isLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] disabled:opacity-40 transition-colors"
                        >
                          <GraduationCap className="w-3.5 h-3.5" />
                          Aprender
                        </button>
                        <button
                          onClick={startPlay}
                          disabled={engineState.isLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-[var(--bordona-accent)] text-white hover:bg-[var(--bordona-accent-hover)] disabled:opacity-40 transition-colors"
                        >
                          <Headphones className="w-3.5 h-3.5" />
                          Practicar
                        </button>
                      </>
                    )}

                    {/* Controles en modo APRENDER */}
                    {subMode === "learn" && (
                      <>
                        {!isPlaying && !practiceFinished ? (
                          <button
                            onClick={async () => {
                              setIsPlaying(true);
                              setPracticeStartTime(Date.now());
                              // Reiniciar detector en tiempo real
                              await startRtDetector((chord) => {
                                if (!chord) return;
                                const expectedEvent = chordSequence[currentChordIndexRef.current];
                                if (!expectedEvent) return;
                                const expected = expectedEvent.chord;
                                const parsedExpected = parseChordName(expected);
                                if (!parsedExpected) return;
                                const detectedName = chord.name;
                                const expectedName = formatChordNameForCompare(parsedExpected);
                                if (chordsAreEquivalent(detectedName, expectedName) && chord.confidence > CONFIDENCE_THRESHOLDS.PLAY_MODE_INLINE_RESUME) {
                                  setDetectedChord(expected);
                                  setPracticeAttempts((a) => a + 1);
                                  setPracticeScore((s) => s + 1);
                                  const next = currentChordIndexRef.current + 1;
                                  if (next >= chordSequence.length) {
                                    stopPracticeRef.current();
                                    return;
                                  }
                                  setCurrentChordIndex(next);
                                  setDetectedChord(null);
                                }
                              });
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-[#10B981] text-white hover:bg-[#059669] transition-colors"
                          >
                            <Mic className="w-3.5 h-3.5" />
                            Escuchar
                          </button>
                        ) : isPlaying ? (
                          <button
                            onClick={stopPractice}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-[#EF4444] text-white hover:bg-[#DC2626] transition-colors animate-pulse"
                          >
                            <MicOff className="w-3.5 h-3.5" />
                            Detener
                          </button>
                        ) : (
                          <button
                            onClick={resetPractice}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-white/[0.07] text-slate-300 hover:bg-white/10 transition-colors"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Reiniciar
                          </button>
                        )}
                        {isPlaying && (
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                            <Music className="w-3 h-3" />
                            <span className="font-semibold text-[var(--bordona-accent)]">{currentChord || "-"}</span>
                            <span>{practiceScore}/{practiceAttempts}</span>
                            <span>{Math.floor(practiceElapsed / 60)}:{String(practiceElapsed % 60).padStart(2, "0")}</span>
                            {/* Acorde detectado en tiempo real */}
                            {rtState.detectedChord && (
                              <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold transition-all ${
                                (() => {
                                  const parsed = currentChord ? parseChordName(currentChord) : null;
                                  return parsed && chordsAreEquivalent(rtState.detectedChord!.name, formatChordNameForCompare(parsed));
                                })()
                                  ? "bg-[#10B981] text-white"
                                  : "bg-white/15 text-slate-300"
                              }`}>
                                {rtState.detectedChord.name}
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Controles en modo PRACTICAR */}
                    {subMode === "play" && (
                      <>
                        {!isPlaying && !practiceFinished ? (
                          <button
                            onClick={async () => {
                              setIsPlaying(true);
                              setPracticeStartTime(Date.now());
                              // Reiniciar detector en tiempo real
                              await startRtDetector((chord) => {
                                if (!chord) return;
                                const expectedEvent = chordSequence[currentChordIndexRef.current];
                                if (!expectedEvent) return;
                                const expected = expectedEvent.chord;
                                const parsedExpected = parseChordName(expected);
                                if (!parsedExpected) return;
                                const detectedName = chord.name;
                                const expectedName = formatChordNameForCompare(parsedExpected);
                                if (chordsAreEquivalent(detectedName, expectedName) && chord.confidence > CONFIDENCE_THRESHOLDS.PLAY_MODE_INLINE_START) {
                                  setDetectedChord(expected);
                                  setDetectedInCurrentBar(true);
                                }
                              });
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-[#10B981] text-white hover:bg-[#059669] transition-colors"
                          >
                            <Play className="w-3.5 h-3.5" />
                            Iniciar
                          </button>
                        ) : isPlaying ? (
                          <button
                            onClick={stopPractice}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-[#EF4444] text-white hover:bg-[#DC2626] transition-colors animate-pulse"
                          >
                            <Pause className="w-3.5 h-3.5" />
                            Pausar
                          </button>
                        ) : (
                          <button
                            onClick={resetPractice}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-sans font-medium bg-white/[0.07] text-slate-300 hover:bg-white/10 transition-colors"
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                            Reiniciar
                          </button>
                        )}
                        {/* BPM control */}
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-semibold text-slate-400">BPM</span>
                          <input
                            type="range"
                            min={40}
                            max={200}
                            value={bpm}
                            onChange={(e) => handleBpmChange(Number(e.target.value))}
                            className="w-16 h-1 rounded-lg appearance-none bg-white/20 accent-[var(--bordona-accent)]"
                          />
                          <span className="text-[10px] font-bold text-slate-100 w-6">{bpm}</span>
                        </div>
                        {/* Metrónomo visual */}
                        {isPlaying && (
                          <div className="flex items-center gap-1">
                            {[0, 1, 2, 3].map((b) => (
                              <div
                                key={b}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  b === currentBeat ? "bg-[var(--bordona-accent)] scale-125" : "bg-white/20"
                                }`}
                              />
                            ))}
                          </div>
                        )}
                        {/* Espectro de cuerdas + Indicador micrófono */}
                        {isPlaying && (
                          <>
                            <div className="w-px h-5 bg-white/15" />
                            <StringSpectrum
                              energies={spectrumEnergies}
                              expectedStrings={currentPracticeVoicing?.positions.map((p) => p.string) || []}
                            />
                            <MicStatusIndicator
                              state={detectedChord ? "detected" : micVolume > 0.05 ? "listening" : "idle"}
                              detectedChord={detectedChord || undefined}
                              volume={micVolume}
                            />
                            {/* Badge de acorde detectado en tiempo real */}
                            {rtState.detectedChord && (
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold transition-all ${
                                (() => {
                                  const parsed = currentChord ? parseChordName(currentChord) : null;
                                  return parsed && rtState.detectedChord!.name === formatChordNameForCompare(parsed);
                                })()
                                  ? "bg-[#10B981] text-white"
                                  : "bg-white/15 text-slate-300"
                              }`}>
                                {rtState.detectedChord.name}
                              </span>
                            )}
                          </>
                        )}
                      </>
                    )}

                    <div className="w-px h-5 bg-white/15" />

                    {/* Guardar rápido (sobre archivo actual) */}
                    {selectedSongId && hasUnsavedChanges && (
                      <button
                        onClick={() => handleSave()}
                        disabled={isSaving}
                        className="relative w-8 h-8 rounded-full bg-[var(--bordona-accent)] flex items-center justify-center text-white hover:bg-[var(--bordona-accent-hover)] transition-colors disabled:opacity-50"
                        title="Guardar cambios"
                      >
                        <Save className="w-3.5 h-3.5" />
                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[var(--bordona-accent)] rounded-full border-2 border-slate-900" />
                      </button>
                    )}

                    {/* Guardar / Imprimir */}
                    <button
                      onClick={() => setSaveOpen(true)}
                      className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/10 transition-colors"
                      title={selectedSongId ? "Guardar como nueva" : "Guardar"}
                    >
                      <Save className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => window.print()}
                      className="w-8 h-8 rounded-full bg-white/[0.07] flex items-center justify-center text-slate-300 hover:bg-white/10 transition-colors print:hidden"
                      title="Imprimir"
                    >
                      <Printer className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Mini reproductor de YouTube flotante */}
                {youtubeUrl && extractYouTubeId(youtubeUrl) && (
                  <div className="absolute bottom-20 right-4 z-30">
                    <YouTubeMiniPlayer
                      ref={youtubePlayerRef}
                      videoId={extractYouTubeId(youtubeUrl)!}
                    />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </LearnWorkbenchShell>
    </>
  );
};

export default SongbookPage;



